import axios from 'axios';
import { CardViewStatsParams, CardViewStatsResponseDto } from '../types/cardViewStatsTypes';
 
import {
    AddMembersInChatRoom,
    AddRatingRequestDto,
    CardResponseDto,
    CardViewRequestDto,
    ChatRequestDto,
    ChatRoom,
    ChatRoomsResponseDto,
    ClosedDealPaymentsResponse,
    CommentRequestDto,
    CommentResponseDto,
    CommonProfileDto,
    ContactInvitesResponseDto,
    ContactsResponseDto,
    CreateAbuseReportRequest,
    CreateChatRoomRequest,
    CreateEventRequest,
    CreateMatchupRequest,
    CustomGoogleLoginResponseDto,
    CustomPrefilledResponseDto,
    DealAgreementResponseDto,
    DealDisputeRequestDto,
    DealResponseDto,
    DealsProposalsRequestDto,
    DocumentReviewRequestDto,
    DocumentUploadRequestDto,
    DocumentUploadResponseDto,
        EventDetailResponse,
        EventGraphDetailResponse,
    DealInsightsDto,
        EventDashboardStatsDto,
        FighterDashboardStatsDto,
    DashboardPendingActionsResponseDto,
    EventListResponse,
    EventShareHistoryResponse,
    FighterManagerLinkRequestDto,
    FighterManagerLinkResponseDto,
    FileMetadata,
    FileMetadataDto,
    GetratingResponseDto,
    LoginDto,
    LoginResponse,
    MarketplaceCard,
    MarketplaceCardResponseDto,
    MatchupResponse,
    MessageDto,
    MessageResponse,
    MessageResponseDto,
        MoneyFlowRange,
    MoneyFlowResponseDto,
    TotalFundsDto,
    NotificationSettingRequestDto,
    OAuthProvider,
    OpportunityCard,
    OpportunityCardDto,
    OpportunityCardFilterParams,
    OpportunityCardRequestDto,
    OpportunityCardTemplate,
    Page,
    PaymentResponseDto,
    PostLikeResponseDto,
    PostRequestDto,
    PostResponseDto,
    Profile,
    ProfileDto,
    ProfileId,
    ProfileResponse,
    ProfileViewRequestDto,
    RatingResponseDto,
    RegionResponse,
    RegisterDto,
    ShareEventRequest,
    StripeAccountStatusDto,
    StripeStatusDto,
    TrackingResponseDto,
    UpdateBoutOrderRequest,
    UpdateDealsProposalsRequestDto,
    UpdateEventRequest,
    UpdateMatchupRequest,
    UpdateOpportunityCardApiDto,
    UpdateOpportunityCardDto,
    UpdateUserDto
} from '../types/types';
import { isValidUrl } from '../utils/utilMethods';
 
const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8010/api/v1';
export const WEBSOCKET_URL = process.env.REACT_APP_WEBSOCKET_URL || "http://localhost:8006/ws";
export const CHAT_WEBSOCKET_URL = process.env.REACT_APP_CHAT_WEBSOCKET_URL || "http://localhost:8007/ws";
export const PROFILE_COUNT_WEBSOCKET_URL = process.env.REACT_APP_PROFILE_COUNT_WEBSOCKET_URL || "http://localhost:8005/ws";
export const DOMAIN_URL = process.env.REACT_APP_DOMAIN_URL || "http://localhost:3000";
 
// Where social sign in starts. This is the address of the BACKEND that serves
// the /oauth2/** paths, which is user-service. It is NOT the website address,
// even though the two are the same string in production.
//
// They match in production only because Nginx proxies /oauth2/** on the website
// domain through to user-service. That makes DOMAIN_URL a safe default there,
// but it is a coincidence of the server setup, not the meaning of the value.
//
// So: if REACT_APP_DOMAIN_URL is ever pointed somewhere that does not proxy
// /oauth2/**, social sign in breaks silently and this must be set explicitly.
// On a laptop there is no proxy at all, which is why frontend/.env sets it to
// http://localhost:8003.
export const OAUTH_BASE_URL = process.env.REACT_APP_OAUTH_BASE_URL || DOMAIN_URL;
 
 
axios.interceptors.response.use(
    response => response,
    async error => {
        if (error.response && error.response.status === 401) {
            try {
                await logoutUser();
                localStorage.clear();
                window.location.href = '/login';
            } catch (logoutError) {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);
 
export const getAuthToken = (): string | null => {
    const userJson = localStorage.getItem('loginResponse');
    const user = userJson ? JSON.parse(userJson) : null;
    return user?.token || null;
};
 
const getCommonHeaders = (includeAuth: boolean = false): Record<string, string> => {
    const headers: Record<string, string> = {
        'Accept-Language': localStorage.getItem('i18nextLng') || 'en',
    };
    if (includeAuth) {
        const token = getAuthToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
    }
    return headers;
};
 
export const handleOAuthSignups = (provider: OAuthProvider): void => {
    const oauth2Endpoints: Record<OAuthProvider, string> = {
        google: `${OAUTH_BASE_URL}/oauth2/authorization/google`,
        facebook: `${OAUTH_BASE_URL}/oauth2/authorization/facebook`,
    };
 
    if (oauth2Endpoints[provider]) {
        localStorage.setItem('oauth_redirect_after', window.location.pathname);
        window.location.href = oauth2Endpoints[provider];
    } else {
        throw new Error('Invalid OAuth provider');
    }
};
 
export const verifyOAuthUser = async (): Promise<CustomGoogleLoginResponseDto> => {
    try {
        const response = await axios.get<CustomGoogleLoginResponseDto>(`${BASE_URL}/user/verify/oauth`, {
            withCredentials: true,
            headers: getCommonHeaders(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};
 
export const verifyNormalUser = async (registerDto: RegisterDto): Promise<CustomGoogleLoginResponseDto> => {
    try {
        const response = await axios.post<CustomGoogleLoginResponseDto>(
            `${BASE_URL}/user/auth/verify-normal`,
            registerDto,
            {
                headers: getCommonHeaders(),
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};
 
export const fetchCountries = async (): Promise<string[]> => {
    try {
        const response = await axios.get<string[]>(`${BASE_URL}/user/countries`, {
            headers: getCommonHeaders(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};
 
export const fetchCitiesByCountry = async (
  country: string,
): Promise<string[]> => {
  try {
    const response = await axios.get<string[]>(
      `${BASE_URL}/user/countries/cities`,
      {
        params: { country },
        headers: getCommonHeaders(),
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchRegionByCountry = async (
  country: string,
): Promise<RegionResponse> => {
  try {
    const response = await axios.get<RegionResponse>(
      `${BASE_URL}/user/countries/region`,
      {
        params: { country },
        headers: getCommonHeaders(),
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const handleSignUp = async (
  registerDto: RegisterDto,
  profileImage?: File,
  video?: File,
): Promise<any> => {
  try {
    const formData = new FormData();
    formData.append(
      "registerDto",
      new Blob([JSON.stringify(registerDto)], { type: "application/json" }),
    );
    if (profileImage) {
      formData.append("profileImage", profileImage);
    }
    if (video) {
      formData.append("video", video);
    }
    const response = await axios.post(`${BASE_URL}/user`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        ...getCommonHeaders(),
      },
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const handleNormalSignupMethod = async (
  registerDto: RegisterDto,
  profileImage?: File,
  video?: File,
): Promise<any> => {
  try {
    console.log("Normal Signup Register DTO:", registerDto);

    const formData = new FormData();
    formData.append(
      "registerDto",
      new Blob([JSON.stringify(registerDto)], { type: "application/json" }),
    );

    if (profileImage) {
      formData.append("profileImage", profileImage);
    }

    if (video) {
      formData.append("video", video);
    }

    const response = await axios.post(
      `${BASE_URL}/user/normal-register`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          ...getCommonHeaders(),
        },
        timeout: 20000,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Normal Signup Error:", error);
    throw error;
  }
};

export const loginWithCredentials = async (
  loginData: LoginDto,
): Promise<LoginResponse> => {
  try {
    const response = await axios.post<LoginResponse>(
      `${BASE_URL}/user/login`,
      loginData,
      {
        withCredentials: true,
        headers: getCommonHeaders(),
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const loginWithNormalCredentials = async (
  loginData: LoginDto,
): Promise<LoginResponse> => {
  try {
    const response = await axios.post<LoginResponse>(
      `${BASE_URL}/user/normal-login`,
      loginData,
      {
        withCredentials: true,
        headers: getCommonHeaders(),
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const checkAuthentication = async (): Promise<boolean> => {
  try {
    const response = await axios.get<boolean>(
      `${BASE_URL}/user/check-authentication`,
      {
        withCredentials: true,
        headers: getCommonHeaders(),
      },
    );
    return response.data;
  } catch (error) {
    return false;
  }
};

export const getLinkedInAuthUrl = async (): Promise<string> => {
  try {
    const response = await axios.get<string>(
      `${BASE_URL}/user/get-linkedinurl`,
      {
        withCredentials: true,
        headers: getCommonHeaders(),
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const handleLinkedInLogin = async (
  code: string,
): Promise<CustomPrefilledResponseDto> => {
  try {
    const response = await axios.post<CustomPrefilledResponseDto>(
      `${BASE_URL}/user/linkedIn?code=${encodeURIComponent(code)}`,
      {},
      {
        withCredentials: true,
        headers: getCommonHeaders(),
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const verifyLinkedInUser = async (
  email: string,
  provider: string,
): Promise<boolean> => {
  try {
    const response = await axios.post<boolean>(
      `${BASE_URL}/user/check-linkedin-user?email=${encodeURIComponent(email)}&provider=${encodeURIComponent(provider)}`,
      {},
      {
        withCredentials: true,
        headers: getCommonHeaders(),
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const logoutUser = async (): Promise<{ message: string }> => {
  try {
    const response = await axios.post<{ message: string }>(
      `${BASE_URL}/user/logout`,
      {},
      {
        withCredentials: true,
        headers: getCommonHeaders(),
      },
    );

    localStorage.removeItem("loginResponse");
    localStorage.removeItem("username");
    return response.data;
  } catch (error) {
    localStorage.removeItem("loginResponse");
    localStorage.removeItem("username");
    throw error;
  }
};

export const getProfile = async (): Promise<string> => {
  try {
    const response = await axios.get<string>(
      `${BASE_URL}/user/profile/welcome`,
      {
        headers: {
          ...getCommonHeaders(true),
          Accept: "*/*",
          ContentType: "application/json",
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getUserProfile = async (): Promise<string> => {
  try {
    const response = await axios.get<string>(`${BASE_URL}/user/profile`, {
      headers: {
        ...getCommonHeaders(true),
        Accept: "*/*",
        ContentType: "application/json",
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export interface FighterFilterParams {
  name?: string;
  location?: string;
  trainingDisciplines?: string[];
  weightClasses?: string[];
  titleHolder?: boolean;
  wins?: number;
  fights?: number;
}

export const fetchFighters = async (
  params: FighterFilterParams,
): Promise<Profile[]> => {
  const queryParams = new URLSearchParams();

  if (params.name) queryParams.append("name", params.name);
  if (params.location) queryParams.append("location", params.location);
  if (params.trainingDisciplines && params.trainingDisciplines.length > 0) {
    params.trainingDisciplines.forEach((td) =>
      queryParams.append("trainingDisciplines", td),
    );
  }
  if (params.weightClasses && params.weightClasses.length > 0) {
    params.weightClasses.forEach((wc) =>
      queryParams.append("weightClasses", wc),
    );
  }
  if (params.titleHolder !== undefined)
    queryParams.append("titleHolder", String(params.titleHolder));
  if (params.wins !== undefined)
    queryParams.append("wins", String(params.wins));
  if (params.fights !== undefined)
    queryParams.append("fights", String(params.fights));

  const url = `${BASE_URL}/management/fighter?${queryParams.toString()}`;

  const response = await axios.get<Profile[]>(url, {
    headers: getCommonHeaders(true),
  });
  return response.data;
};

export const createOpportunityCard = async (
  opportunityCardDto: OpportunityCardDto,
): Promise<MarketplaceCard> => {
  const response = await axios.post<MarketplaceCard>(
    `${BASE_URL}/management/opportunity-card`,
    opportunityCardDto,
    {
      headers: getCommonHeaders(true),
    },
  );
  return response.data;
};

export const updateOpportunityCard = async (
  updatedData: UpdateOpportunityCardDto,
): Promise<MarketplaceCard> => {
  try {
    const response = await axios.put<MarketplaceCard>(
      `${BASE_URL}/management/opportunity-card`,
      updatedData,
      {
        headers: getCommonHeaders(true),
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateOpportunityCards = async (
  updatedData: UpdateOpportunityCardApiDto,
  userName: string,
): Promise<MarketplaceCard> => {
  try {
    const response = await axios.put<MarketplaceCard>(
      `${BASE_URL}/management/opportunity-card`,
      updatedData,
      {
        headers: {
          ...getCommonHeaders(true),
          USERNAME: userName, // 👈 mandatory header
        },
      },
    );

    return response.data;
  } catch (error) {
    console.error("Update opportunity card failed", error);
    throw error;
  }
};

export const updateOpportunityCardStatus = async (
  cardId: string,
): Promise<any> => {
  try {
    const response = await axios.put<any>(
      `${BASE_URL}/management/opportunity-card/${cardId}`,
      {},
      {
        headers: getCommonHeaders(true),
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteOpportunityCard = async (
  cardId: string,
): Promise<MessageDto> => {
  try {
    const response = await axios.delete<MessageDto>(
      `${BASE_URL}/management/opportunity-card/${cardId}`,
      {
        headers: getCommonHeaders(true),
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateUser = async (
  updateUserDto: UpdateUserDto,
): Promise<Profile> => {
  try {
    const response = await axios.put<Profile>(
      `${BASE_URL}/user/profile`,
      updateUserDto,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createOpportunityCards = async (
  opportunityCardDto: OpportunityCardRequestDto,
): Promise<MarketplaceCard> => {
  const response = await axios.post<MarketplaceCard>(
    `${BASE_URL}/management/opportunity-card`,
    opportunityCardDto,
    {
      headers: getCommonHeaders(true),
    },
  );
  return response.data;
};

export const fetchOpportunityCards = async (
  params: OpportunityCardFilterParams,
): Promise<any> => {
  const queryParams = new URLSearchParams();

  if (params.name) queryParams.append("name", params.name);
  if (params.location) queryParams.append("location", params.location);
  if (params.combatSport) queryParams.append("combatSport", params.combatSport);
  if (params.weightClass) queryParams.append("weightClass", params.weightClass);
  if (params.opportunityType)
    queryParams.append("opportunityType", params.opportunityType);
  if (params.purse) queryParams.append("purse", params.purse);
  if (params.titleShot !== undefined)
    queryParams.append("titleShot", String(params.titleShot));
  if (params.myCard !== undefined)
    queryParams.append("myCard", String(params.myCard));
  if (params.isDeal !== undefined)
    queryParams.append("isDeal", String(params.isDeal));
  if (params.page !== undefined)
    queryParams.append("page", String(params.page));
  if (params.size !== undefined)
    queryParams.append("size", String(params.size));

  const url = `${BASE_URL}/management/marketplace/marketplace-cards?${queryParams.toString()}`;

  const response = await axios.get<any>(url, {
    headers: getCommonHeaders(true),
  });

  return response.data;
};

export const fetchPurseOptions = async (): Promise<string[]> => {
  try {
    const response = await axios.get<string[]>(
      `${BASE_URL}/management/constants/purses`,
      {
        headers: getCommonHeaders(true),
      },
    );
    return response.data;
  } catch (error) {
    return [];
  }
};

export const fetchCurrencyOptions = async (): Promise<string[]> => {
  try {
    const response = await axios.get<string[]>(
      `${BASE_URL}/management/constants/currencies`,
      {
        headers: getCommonHeaders(true),
      },
    );
    return response.data;
  } catch (error) {
    return [];
  }
};

export const fetchWeightClasses = async (): Promise<string[]> => {
  try {
    const response = await axios.get<string[]>(
      `${BASE_URL}/management/constants/weight-classes`,
      {
        headers: getCommonHeaders(true),
      },
    );
    return response.data;
  } catch (error) {
    return [];
  }
};

export const fetchOpportunityTypes = async (): Promise<string[]> => {
  try {
    const response = await axios.get<string[]>(
      `${BASE_URL}/management/constants/opportunity-types`,
      {
        headers: getCommonHeaders(true),
      },
    );
    return response.data;
  } catch (error) {
    return [];
  }
};

export const fetchRosterStatus = async (): Promise<string[]> => {
  try {
    const response = await axios.get<string[]>(
      `${BASE_URL}/management/constants/roster-statuses`,
      {
        headers: getCommonHeaders(true),
      },
    );
    return response.data;
  } catch (error) {
    return [];
  }
};

export const fetchTrainingDisciplines = async (): Promise<string[]> => {
  try {
    const response = await axios.get<string[]>(
      `${BASE_URL}/management/constants/training-disciplines`,
      {
        headers: getCommonHeaders(true),
      },
    );
    return response.data;
  } catch (error) {
    return [];
  }
};

export const getManagers = async (params?: {
  name?: string;
  location?: string;
  type?: string;
  status?: string;
  gender?: string;
  page?: number;
  pageSize?: number;
}) => {
  try {
    const response = await axios.get(`${BASE_URL}/management/manager`, {
      params: {
        ...params,
        page: params?.page || 0,
        size: params?.pageSize || 10,
      },
      headers: getCommonHeaders(true),
    });

    return {
      data: Array.isArray(response.data.content) ? response.data.content : [],
      total:
        typeof response.data.totalElements === "number"
          ? response.data.totalElements
          : 0,
      page: typeof response.data.number === "number" ? response.data.number : 0,
      pageSize:
        typeof response.data.size === "number" ? response.data.size : 10,
      totalPages:
        typeof response.data.totalPages === "number"
          ? response.data.totalPages
          : 1,
    };
  } catch (error) {
    return {
      data: [],
      total: 0,
      page: 0,
      pageSize: 10,
      totalPages: 1,
    };
  }
};

export const getFighters = async (params?: {
  name?: string;
  location?: string;
  trainingDiscipline?: string;
  weightClass?: string;
  titleHolder?: boolean;
  wins?: string;
  fights?: number;
  status?: string;
  gender?: string;
  page?: number;
  pageSize?: number;
}) => {
  try {
    const response = await axios.get(`${BASE_URL}/management/fighter`, {
      params: {
        ...params,
        page: params?.page || 0,
        size: params?.pageSize || 2,
      },
      headers: getCommonHeaders(true),
    });

    return {
      data: Array.isArray(response.data.content) ? response.data.content : [],
      total:
        typeof response.data.totalElements === "number"
          ? response.data.totalElements
          : 0,
      page: typeof response.data.number === "number" ? response.data.number : 0,
      pageSize: typeof response.data.size === "number" ? response.data.size : 2,
      totalPages:
        typeof response.data.totalPages === "number"
          ? response.data.totalPages
          : 1,
    };
  } catch (error) {
    console.error("Error fetching fighters:", error);
    return {
      data: [],
      total: 0,
      page: 0,
      pageSize: 2,
      totalPages: 1,
    };
  }
};
export const getReadyRosterCards = async (params?: {
  fighterId?: number;
  name?: string;
  location?: string;
  combatSport?: string;
  weightClass?: string;
  wins?: string;
  titleHolder?: boolean;
  fights?: number;
  page?: number;
  size?: number;
}) => {
  try {
    const queryParams: Record<string, any> = {
      page: 0,
      size: 10,
      ...params,
    };

    if (params?.combatSport) {
      queryParams.combatSport = params.combatSport;
    }

    if (params?.weightClass) {
      queryParams.weightClass = params.weightClass;
    }

    if (params?.wins) {
      queryParams.wins = params.wins;
    }

    if (params?.titleHolder !== undefined) {
      queryParams.titleHolder = params.titleHolder;
    }

    if (params?.fights) {
      queryParams.fights = params.fights;
    }

    const response = await axios.get(`${BASE_URL}/management/ready-roster`, {
      params: queryParams,
      paramsSerializer: (params) => new URLSearchParams(params).toString(),
      headers: getCommonHeaders(true),
    });

    if (
      response.data &&
      response.data.content &&
      Array.isArray(response.data.content)
    ) {
      return {
        content: response.data.content,
        totalElements: response.data.totalElements,
        totalPages: response.data.totalPages,
        pageNumber: response.data.number,
        pageSize: response.data.size,
        first: response.data.first,
        last: response.data.last,
      };
    }

    return {
      content: [],
      totalElements: 0,
      totalPages: 0,
      pageNumber: 0,
      pageSize: 10,
      first: true,
      last: true,
    };
  } catch (error) {
    console.error("Error fetching ready roster cards:", error);
    return {
      content: [],
      totalElements: 0,
      totalPages: 0,
      pageNumber: 0,
      pageSize: 10,
      first: true,
      last: true,
    };
  }
};

export const createReadyRosterCard = async (data: any) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/management/ready-roster`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
          ...getCommonHeaders(true),
        },
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getReadyRosterCard = async (cardId: any) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/management/ready-roster/${cardId}`,
      {
        headers: {
          "Content-Type": "application/json",
          ...getCommonHeaders(true),
        },
      },
    );

    return response.data;
  } catch (error: any) {
    throw error.response?.data || error;
  }
};

export const getOpportunityCard = async (cardId: any) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/management/opportunity-card/${cardId}`,
      {
        headers: {
          "Content-Type": "application/json",
          ...getCommonHeaders(true),
        },
      },
    );

    return response.data;
  } catch (error: any) {
    throw error.response?.data || error;
  }
};

export const updateReadyRosterCard = async (data: any) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/management/ready-roster`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
          ...getCommonHeaders(true),
        },
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteReadyRosterCard = async (cardId: string | number) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/management/ready-roster/${cardId}`,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );

    return response.data;
  } catch (error) {
    console.error("Error deleting ready roster card:", error);
    throw error;
  }
};

export const getAllNotifications = async (days: number = 7) => {
  try {
    const response = await axios.get(`${BASE_URL}/notification`, {
      headers: {
        "Content-Type": "application/json",
        ...getCommonHeaders(true),
      },
      params: { days },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllChatNotifications = async (days: number = 7) => {
  try {
    const response = await axios.get(`${BASE_URL}/notification/chat`, {
      headers: {
        "Content-Type": "application/json",
        ...getCommonHeaders(true),
      },
      params: { days },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createDealProposal = async (
  dealData: DealsProposalsRequestDto,
) => {
  try {
    const response = await axios.post(`${BASE_URL}/deal`, dealData, {
      headers: {
        "Content-Type": "application/json",
        USERNAME: JSON.parse(localStorage.getItem("loginResponse") || "{}")
          .username,
        ...getCommonHeaders(true),
      },
    });

    if (!response.data) {
      throw new Error("Failed to create deal proposal");
    }

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateDealStatus = async (
  dealId: string | number,
  status: "CONFIRMED" | "REJECTED" | "CANCELLED" | "ACCEPTED",
): Promise<DealResponseDto> => {
  try {
    const response = await axios.put<DealResponseDto>(
      `${BASE_URL}/deal/status`,
      null,
      {
        params: {
          dealId,
          status,
        },
        headers: {
          "Content-Type": "application/json",
          ...getCommonHeaders(true),
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createContact = async (
  type: string,
  cardId?: number | string,
): Promise<any> => {
  try {
    const params = new URLSearchParams();

    if (type === "readyroster" && cardId !== undefined) {
      params.append("marketplaceCardId", cardId.toString());
    } else if (type === "opportunity" && cardId !== undefined) {
      params.append("marketplaceCardId", cardId.toString());
    }

    const query = params.toString();

    const fullUrl = `${BASE_URL}/management/contacts${query ? "?" + query : ""}`;

    const response = await axios.post<any>(
      fullUrl,
      {},
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateReadyRosterCardStatus = async (
  cardId: string | number,
): Promise<any> => {
  try {
    const response = await axios.put<any>(
      `${BASE_URL}/management/ready-roster/${cardId}`,
      {},
      {
        headers: getCommonHeaders(true),
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateNotificationViewStatus = async (
  notificationId: number,
): Promise<void> => {
  try {
    await axios.put(
      `${BASE_URL}/notification/status/${notificationId}`,
      {},
      {
        headers: {
          "Content-Type": "application/json",
          ...getCommonHeaders(true),
        },
      },
    );
  } catch (error) {
    throw error;
  }
};

export const uploadMediaFile = async (
  file: File,
  fieldName: string = "file",
): Promise<string> => {
  const formData = new FormData();
  formData.append(fieldName, file);

  try {
    const response = await axios.post(
      `${BASE_URL}/user/upload-file`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          ...getCommonHeaders(true),
        },
      },
    );

    if (typeof response.data === "string" && isValidUrl(response.data)) {
      return response.data;
    } else if (
      response.data &&
      typeof response.data === "object" &&
      response.data.url
    ) {
      return response.data.url;
    } else {
      return response.data;
    }
  } catch (error) {
    throw error;
  }
};

export const fetchProfile = async (): Promise<Profile | null> => {
  try {
    const response = await axios.get<Profile>(`${BASE_URL}/user/profile`, {
      headers: {
        ...getCommonHeaders(true),
        USERNAME: localStorage.getItem("username") || "",
      },
    });
    return response.data;
  } catch (error) {
    return null;
  }
};

export interface FetchActivityFilters {
  title?: string;
  startDate?: string;
  endDate?: string;
  description?: string;
  page?: number;
  size?: number;
}

export const fetchActivities = async (
  filters: FetchActivityFilters = {},
): Promise<any[]> => {
  try {
    const queryParams = new URLSearchParams();
    if (filters.title) queryParams.append("title", filters.title);
    if (filters.startDate) queryParams.append("startDate", filters.startDate);
    if (filters.endDate) queryParams.append("endDate", filters.endDate);
    if (filters.description)
      queryParams.append("description", filters.description);
    if (filters.page !== undefined)
      queryParams.append("page", String(filters.page));
    if (filters.size !== undefined)
      queryParams.append("size", String(filters.size));

    const response = await axios.get(
      `${BASE_URL}/management/activities?${queryParams.toString()}`,
      {
        headers: getCommonHeaders(true),
      },
    );

    return response.data?.content || response.data || [];
  } catch (error) {
    return [];
  }
};

export const fetchActivityTitles = async (): Promise<string[]> => {
  try {
    const response = await axios.get(
      `${BASE_URL}/management/constants/activity-titles`,
      {
        headers: getCommonHeaders(true),
      },
    );
    return response.data || [];
  } catch (error) {
    return [];
  }
};

export const updateDealDetails = async (
  dto: UpdateDealsProposalsRequestDto,
): Promise<DealResponseDto> => {
  try {
    const response = await axios.put<DealResponseDto>(`${BASE_URL}/deal`, dto, {
      headers: {
        ...getCommonHeaders(true),
        USERNAME: JSON.parse(localStorage.getItem("loginResponse") || "{}")
          .username,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllMessages = async (
  receiverId: string | number,
  type: string,
): Promise<MessageResponseDto[]> => {
  try {
    const response = await axios.get<MessageResponseDto[]>(
      `${BASE_URL}/chat/all/${receiverId}/${type}`,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const sendMessage = async (payload: ChatRequestDto): Promise<any> => {
  try {
    const response = await axios.post(`${BASE_URL}/chat`, payload, {
      headers: {
        ...getCommonHeaders(true),
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getContactsMadeByUser = async (): Promise<ContactsResponseDto> => {
  try {
    const response = await axios.get<ContactsResponseDto>(
      `${BASE_URL}/management/contacts`,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchWeightClasses_signup = async (): Promise<string[]> => {
  try {
    const response = await axios.get<string[]>(
      `${BASE_URL}/user/enum/weight-classes`,
      {
        headers: getCommonHeaders(true),
      },
    );
    return response.data;
  } catch (error) {
    return [];
  }
};

export const getAllProfiles = async (
  search?: string,
): Promise<ProfileResponse[]> => {
  try {
    const response = await axios.get<ProfileResponse[]>(
      `${BASE_URL}/user/profile/all`,
      {
        headers: getCommonHeaders(true),
        params: search ? { search } : {},
      },
    );
    return response.data;
  } catch (error) {
    return [];
  }
};

export const getProfileById = async (profileId: string | number) => {
  try {
    const response = await axios.get(`${BASE_URL}/user/public`, {
      params: { profileId },
    });

    return {
      success: true,
      data: response.data as Profile,
    };
  } catch (error) {
    return {
      success: false,
      data: null,
    };
  }
};

export const fetchTrainingDisciplines_signup = async (): Promise<string[]> => {
  try {
    const response = await axios.get<string[]>(
      `${BASE_URL}/user/enum/training-disciplines`,
      {
        headers: getCommonHeaders(true),
      },
    );
    return response.data;
  } catch (error) {
    return [];
  }
};

export const fetchSocialMediaPlatforms = async (): Promise<string[]> => {
  try {
    const response = await axios.get<string[]>(
      `${BASE_URL}/user/enum/social-media-platforms`,
      {
        headers: getCommonHeaders(true),
      },
    );
    return response.data;
  } catch (error) {
    return [];
  }
};

export const uploadMediaFromUrl = async (imageUrl: string): Promise<string> => {
  try {
    const response = await axios.post(`${BASE_URL}/user/upload-url`, null, {
      params: { url: imageUrl },
    });

    if (typeof response.data === "string" && isValidUrl(response.data)) {
      return response.data;
    } else {
      throw new Error("Invalid response format from upload-url API");
    }
  } catch (error) {
    throw error;
  }
};

export const changeLanguage = async (language: string): Promise<string> => {
  try {
    const response = await axios.post<string>(
      `${BASE_URL}/user/profile/language?language=${encodeURIComponent(language)}`,
      null,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getContactedCount = async (): Promise<number> => {
  try {
    const response = await axios.get<number>(
      `${BASE_URL}/chat/contacted-count`,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createChatRoom = async (
  payload: CreateChatRoomRequest,
): Promise<ChatRoom> => {
  try {
    const response = await axios.post<ChatRoom>(
      `${BASE_URL}/chat/room`,
      payload,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const leaveChatRoom = async (
  roomId: number,
  username: string,
): Promise<ChatRoom> => {
  try {
    const response = await axios.put<ChatRoom>(
      `${BASE_URL}/chat/room/leave/${roomId}`,
      null,
      {
        headers: {
          USERNAME: username,
          ...getCommonHeaders(true),
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getUserChatRooms = async (): Promise<ChatRoomsResponseDto> => {
  try {
    const response = await axios.get<ChatRoomsResponseDto>(
      `${BASE_URL}/chat/room`,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getGroupMessages = async (
  chatRoomId: number,
): Promise<MessageResponseDto[]> => {
  try {
    const response = await axios.get<MessageResponseDto[]>(
      `${BASE_URL}/chat/room/${chatRoomId}`,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const uploadMultipleFile = async (
  files: File[],
  fieldName: string = "file",
): Promise<FileMetadata[]> => {
  const formData = new FormData();

  files.forEach((file) => {
    formData.append(fieldName, file);
  });

  try {
    const response = await axios.post(
      `${BASE_URL}/user/upload-chat-file`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          ...getCommonHeaders(true),
        },
      },
    );

    if (Array.isArray(response.data)) {
      return response.data as FileMetadata[];
    } else {
      throw new Error("Invalid response format from upload API");
    }
  } catch (error) {
    throw error;
  }
};

export const createAbuseReport = async (
  payload: CreateAbuseReportRequest,
): Promise<string> => {
  try {
    const response = await axios.post<string>(
      `${BASE_URL}/management/reports`,
      payload,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllInvites = async (): Promise<ContactInvitesResponseDto> => {
  try {
    const response = await axios.get<ContactInvitesResponseDto>(
      `${BASE_URL}/management/contacts/invites`,
      {
        headers: {
          "Content-Type": "application/json",
          ...getCommonHeaders(true),
        },
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getMyInvites = async (): Promise<ContactInvitesResponseDto> => {
  try {
    const response = await axios.get<ContactInvitesResponseDto>(
      `${BASE_URL}/management/contacts/request`,
      {
        headers: {
          "Content-Type": "application/json",
          ...getCommonHeaders(true),
        },
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateInvites = async (contactId: number, status: boolean) => {
  try {
    const response = await axios.put(`${BASE_URL}/management/contacts`, null, {
      params: {
        contactId,
        status,
      },
      headers: {
        "Content-Type": "application/json",
        ...getCommonHeaders(true),
      },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateChatRoom = async (
  chatRoomId: number,
  payload: CreateChatRoomRequest,
): Promise<ChatRoom> => {
  try {
    const response = await axios.put<ChatRoom>(
      `${BASE_URL}/chat/room/${chatRoomId}`,
      payload,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateFighterManagerLinkStatus = async (
  username: string,
  linkId: number,
  approved: boolean,
): Promise<string> => {
  try {
    const response = await axios.patch<string>(
      `${BASE_URL}/user/link-status`,
      null,
      {
        headers: {
          ...getCommonHeaders(true),
          USERNAME: username,
        },
        params: {
          linkId,
          approved,
        },
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getFightersWithoutActiveLink = async (): Promise<ProfileDto[]> => {
  try {
    const response = await axios.get<ProfileDto[]>(
      `${BASE_URL}/user/no-active-manager-link`,
      {
        headers: {
          ...getCommonHeaders(true),
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getFighterManagerLinks = async (
  username: string,
): Promise<FighterManagerLinkResponseDto[]> => {
  try {
    const response = await axios.get<FighterManagerLinkResponseDto[]>(
      `${BASE_URL}/user/manager-links`,
      {
        headers: {
          ...getCommonHeaders(true),
          Accept: "application/json",
          "Content-Type": "application/json",
          USERNAME: username,
        },
      },
    );
    if (!Array.isArray(response.data)) {
      throw new Error("API response is not an array");
    }
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createFighterManagerLink = async (
  username: string,
  dto: FighterManagerLinkRequestDto,
): Promise<string> => {
  try {
    const response = await axios.post<string>(
      `${BASE_URL}/user/fighter-manager-link`,
      dto,
      {
        headers: {
          ...getCommonHeaders(true),
          USERNAME: username,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createProfileView = async (
  dto: ProfileViewRequestDto,
): Promise<string> => {
  try {
    const response = await axios.post<string>(
      `${BASE_URL}/tracking/public/profile-view`,
      dto,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createPublicProfileView = async (
  dto: ProfileViewRequestDto,
): Promise<string> => {
  try {
    const response = await axios.post<string>(
      `${BASE_URL}/tracking/public/profile-view`,
      dto,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createCardView = async (
  username: string,
  dto: CardViewRequestDto,
): Promise<string> => {
  try {
    const response = await axios.post<string>(
      `${BASE_URL}/tracking/card-view`,
      dto,
      {
        headers: {
          ...getCommonHeaders(true),
          USERNAME: username,
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProfileViewStats = async (
  username: string,
  range?: "7days" | "month" | "all",
): Promise<TrackingResponseDto> => {
  try {
    const response = await axios.get<TrackingResponseDto>(
      `${BASE_URL}/tracking/profile-view-stats`,
      {
        headers: {
          ...getCommonHeaders(true),
          USERNAME: username,
        },
        params: range === "7days" || !range ? {} : { range },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateNotificationSetting = async (
  username: string,
  dto: NotificationSettingRequestDto,
): Promise<string> => {
  try {
    const response = await axios.post<string>(
      `${BASE_URL}/user/profile/setting/notification`,
      dto,
      {
        headers: {
          ...getCommonHeaders(true),
          USERNAME: username,
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProfileForSetting = async (
  username: string,
): Promise<Profile> => {
  try {
    const response = await axios.get<Profile>(`${BASE_URL}/user/profile`, {
      headers: {
        ...getCommonHeaders(true),
        USERNAME: username,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProfileUser = async (
  profileId: string | number,
  username: string,
): Promise<Profile> => {
  try {
    const response = await axios.get<Profile>(`${BASE_URL}/user`, {
      headers: {
        ...getCommonHeaders(true),
        USERNAME: username,
      },
      params: {
        profileId,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllSubregions = async (): Promise<string[]> => {
  try {
    const response = await axios.get<string[]>(
      `${BASE_URL}/user/countries/subregions`,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getConfirmedDeals = async (
  status?: string,
  fromDate?: string,
  toDate?: string,
  page: number = 0,
  size: number = 10,
): Promise<DealResponseDto[]> => {
  try {
    const response = await axios.get<DealResponseDto[]>(`${BASE_URL}/deal`, {
      headers: {
        "Content-Type": "application/json",
        USERNAME: JSON.parse(localStorage.getItem("loginResponse") || "{}")
          .username,
        ...getCommonHeaders(true),
      },

      params: {
        status,
        fromDate,
        toDate,
        page,
        size,
      },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateMembersInGroup = async (
  payload: AddMembersInChatRoom,
): Promise<ChatRoom> => {
  try {
    const response = await axios.patch<ChatRoom>(
      `${BASE_URL}/chat/room`,
      payload,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const removeUserFromChatRoom = async (
  roomId: number,
  userId: ProfileId,
): Promise<ChatRoom> => {
  try {
    const response = await axios.put<ChatRoom>(
      `${BASE_URL}/chat/room/${roomId}/${userId}`,
      null,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateActivationRequired = async (
  username: string,
): Promise<string> => {
  try {
    const response = await axios.put<string>(
      `${BASE_URL}/user/profile/setting/activation`,
      null,
      {
        headers: {
          ...getCommonHeaders(true),
          USERNAME: username,
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllPosts = async (
  username: string,
  isMyPost: boolean = false,
  filters?: {
    title?: string;
    trainingDiscipline?: string;
    weightClass?: string;
    managerType?: string;
    profileType?: string;
  },
): Promise<{ posts: PostResponseDto[]; profiles: CommonProfileDto[] }> => {
  try {
    const params: any = { isMyPost };
    if (filters) {
      Object.keys(filters).forEach((key) => {
        if (filters[key as keyof typeof filters] !== undefined) {
          params[key] = filters[key as keyof typeof filters];
        }
      });
    }

    const response = await axios.get<{
      posts: PostResponseDto[];
      profiles: CommonProfileDto[];
    }>(`${BASE_URL}/socialmedia/post`, {
      headers: {
        ...getCommonHeaders(true),
        USERNAME: username,
      },
      params,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updatePost = async (
  username: string,
  postId: number,
  dto: PostRequestDto,
): Promise<PostResponseDto> => {
  const response = await axios.put<PostResponseDto>(
    `${BASE_URL}/socialmedia/post/${postId}`,
    dto,
    {
      headers: {
        ...getCommonHeaders(true),
        USERNAME: username,
      },
    },
  );
  return response.data;
};
export const deletePost = async (
  username: string,
  postId: number,
): Promise<MessageDto> => {
  const response = await axios.delete<MessageDto>(
    `${BASE_URL}/socialmedia/post/${postId}`,
    {
      headers: {
        ...getCommonHeaders(true),
        USERNAME: username,
      },
    },
  );
  return response.data;
};

export const createComment = async (
  username: string,
  dto: CommentRequestDto,
): Promise<CommentResponseDto> => {
  const response = await axios.post<CommentResponseDto>(
    `${BASE_URL}/socialmedia/post/comment`,
    dto,
    {
      headers: {
        ...getCommonHeaders(true),
        USERNAME: username,
      },
    },
  );
  return response.data;
};

export const updateComment = async (
  username: string,
  commentId: number,
  dto: CommentRequestDto,
): Promise<CommentResponseDto> => {
  const response = await axios.put<CommentResponseDto>(
    `${BASE_URL}/socialmedia/post/comment/${commentId}`,
    dto,
    {
      headers: {
        ...getCommonHeaders(true),
        USERNAME: username,
      },
    },
  );
  return response.data;
};

export const deleteComment = async (
  username: string,
  commentId: number,
): Promise<MessageDto> => {
  const response = await axios.delete<MessageDto>(
    `${BASE_URL}/socialmedia/post/comment/${commentId}`,
    {
      headers: {
        ...getCommonHeaders(true),
        USERNAME: username,
      },
    },
  );
  return response.data;
};

export const getCommentsByPost = async (
  username: string,
  postId: number,
): Promise<CommentResponseDto[]> => {
  try {
    const response = await axios.get<
      CommentResponseDto[] | Page<CommentResponseDto>
    >(`${BASE_URL}/socialmedia/post/comments`, {
      params: {
        postId,
      },
      headers: {
        ...getCommonHeaders(true),
        USERNAME: username,
      },
    });

    if (Array.isArray(response.data)) {
      return response.data;
    }

    return response.data?.content || [];
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch comments",
      );
    }

    throw new Error("Failed to fetch comments");
  }
};

export const toggleLike = async (
  username: string,
  postId: number,
  isLike: boolean,
): Promise<MessageDto> => {
  const response = await axios.post<MessageDto>(
    `${BASE_URL}/socialmedia/post/${postId}/like`,
    null,
    {
      headers: {
        ...getCommonHeaders(true),
        USERNAME: username,
      },
      params: { postId, isLike },
    },
  );
  return response.data;
};

export const getLikesByPost = async (
  postId: number,
): Promise<PostLikeResponseDto[]> => {
  const response = await axios.get<PostLikeResponseDto[]>(
    `${BASE_URL}/socialmedia/post/${postId}/likes`,

    {
      headers: {
        ...getCommonHeaders(true),
      },
    },
  );
  return response.data;
};

export const createPost = async (
  username: string,
  dto: PostRequestDto,
): Promise<string> => {
  try {
    const response = await axios.post<string>(
      `${BASE_URL}/socialmedia/post`,
      dto,
      {
        headers: {
          ...getCommonHeaders(true),
          USERNAME: username,
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const uploadSingleFile = async (
  file: File,
  fieldName: string = "file",
): Promise<FileMetadataDto> => {
  const formData = new FormData();
  formData.append(fieldName, file);

  try {
    const response = await axios.post<FileMetadataDto>(
      `${BASE_URL}/user/upload-single-file`,
      formData,
      {
        headers: getCommonHeaders(true),
        // headers: {
        //     "Content-Type": "multipart/form-data",
        // },
      },
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const getAllMarketplaceCards = async (
  username: string,
  filters?: {
    name?: string;
    location?: string;
    sport?: string;
    weightClass?: string;
    purse?: string;
    titleShot?: boolean;
    myCard?: boolean;
    opportunityType?: string;
    rosterStatus?: string;
    role?: string;
    isPublished?: boolean;
    isDeal?: boolean;
    fighterId?: string;
    page?: number;
    size?: number;
  },
): Promise<Page<MarketplaceCardResponseDto>> => {
  try {
    const params: any = {};
    if (filters) {
      Object.keys(filters).forEach((key) => {
        const value = filters[key as keyof typeof filters];
        if (value !== undefined && value !== null) {
          params[key] = value;
        }
      });
    }

    const response = await axios.get<Page<MarketplaceCardResponseDto>>(
      `${BASE_URL}/management/marketplace/marketplace-cards`,
      {
        headers: {
          ...getCommonHeaders(true),
          USERNAME: username,
        },
        params,
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPost = async (postId: number): Promise<PostResponseDto> => {
  try {
    const response = await axios.get<PostResponseDto>(
      `${BASE_URL}/socialmedia/post/${postId}`,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllRatings = async (
  profileId: string | number,
  page: number = 0,
  size: number = 10,
): Promise<Page<RatingResponseDto>> => {
  try {
    const response = await axios.get<Page<RatingResponseDto>>(
      `${BASE_URL}/rating/by-profile`,
      {
        headers: {
          ...getCommonHeaders(true),
        },
        params: { page, size, profileId },
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getRatingForDealAndUser = async (
  dealId: string | number,
): Promise<GetratingResponseDto> => {
  try {
    const response = await axios.get<GetratingResponseDto>(
      `${BASE_URL}/rating/${dealId}`,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );

    return response.data;
  } catch (error: any) {
    console.error("Get Rating Error:", error);
    throw (
      error.response?.data?.message || error.message || "Something went wrong"
    );
  }
};

export const getConnectStatus = async (): Promise<string> => {
  try {
    const response = await axios.get<string>(
      `${BASE_URL}/payment/connect/status`,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getConnectDetails = async (): Promise<StripeAccountStatusDto> => {
  try {
    const response = await axios.get<StripeAccountStatusDto>(
      `${BASE_URL}/payment/connect/details`,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const connectStripe = async (): Promise<string> => {
  try {
    const response = await axios.get<string>(
      `${BASE_URL}/payment/connect/onboard`,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const unlinkStripe = async (): Promise<{ message: string }> => {
  try {
    const response = await axios.delete<{ message: string }>(
      `${BASE_URL}/payment/connect/unlink`,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const submitRatingOnly = async (
  ratingData: AddRatingRequestDto,
): Promise<any> => {
  const res = await axios.post(`${BASE_URL}/rating`, ratingData, {
    headers: getCommonHeaders(true),
  });
  return res.data;
};

export const completePayment = async (
  dealId: string | number,
): Promise<Record<string, any>> => {
  try {
    const response = await axios.post<Record<string, any>>(
      `${BASE_URL}/payment/complete`,
      null,
      {
        headers: {
          ...getCommonHeaders(true),
        },
        params: {
          dealId,
        },
      },
    );

    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const createCheckoutSession = async (
  username: string,
  dealId: string | number,
  componentId?: number,
): Promise<Record<string, string>> => {
  try {
    const params: any = { dealId: dealId };
    if (componentId !== undefined) params.componentId = componentId;

    const response = await axios.post<Record<string, string>>(
      `${BASE_URL}/payment/checkout`,
      null,
      {
        headers: {
          ...getCommonHeaders(true),
          username: username,
        },
        params,
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateFightResultOnly = async (
  dealId: string | number,
  fightResult: string | null,
  fighterAccepted?: boolean,
  dispute?: DealDisputeRequestDto,
): Promise<any> => {
  const payload: any = {
    dealId,
    fightResult,
  };

  if (fighterAccepted !== undefined) {
    payload.fighterAccepted = fighterAccepted;

    if (fighterAccepted === false && dispute) {
      payload.dispute = dispute;
    }
  }

  const res = await axios.put(`${BASE_URL}/deal/updateResult`, payload, {
    headers: getCommonHeaders(true),
  });
  return res.data;
};

const wait = (milliseconds: number) =>
  new Promise((resolve) => setTimeout(resolve, milliseconds));

export const waitUntilTokenAvailable = async (): Promise<string | null> => {
  for (let i = 0; i < 20; i++) {
    const token = getAuthToken();

    if (token) {
      console.log("Token used for verify:", token);
      return token;
    }
    console.log("Token is not available yet, retrying...");
    await wait(300);
  }
  console.log("Token not found after maximum retries");
  return null;
};

export const verifyPayment = async (sessionId: string): Promise<any> => {
  try {
    const token = await waitUntilTokenAvailable();

    if (!token) {
      console.error("Token not available after waiting");
      return null;
    }
    const response = await axios.get(`${BASE_URL}/payment/verify`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: { sessionId },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDealById = async (
  dealId: string | number,
): Promise<DealResponseDto> => {
  try {
    const response = await axios.get<DealResponseDto>(
      `${BASE_URL}/deal/${dealId}`,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );

    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data?.message || error.message || "Something went wrong"
    );
  }
};

export const getClosedDealPayments =
  async (): Promise<ClosedDealPaymentsResponse> => {
    try {
      const response = await axios.get<ClosedDealPaymentsResponse>(
        `${BASE_URL}/payment`,
        {
          headers: {
            ...getCommonHeaders(true),
          },
        },
      );

      return response.data;
    } catch (error: any) {
      throw (
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch closed deal payments"
      );
    }
  };

export const createDispute = async (
  disputeData: DealDisputeRequestDto,
): Promise<any> => {
  try {
    const response = await axios.post(`${BASE_URL}/deal/dispute`, disputeData, {
      headers: {
        ...getCommonHeaders(true),
      },
    });

    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data?.message ||
      error.message ||
      "Failed to create dispute"
    );
  }
};

export const downloadDealOverviewPdf = async (
  dealId: string | number,
): Promise<Blob> => {
  const loginResponse = JSON.parse(
    localStorage.getItem("loginResponse") || "{}",
  );
  if (!loginResponse?.username && !loginResponse?.email) {
    throw new Error("User not authenticated");
  }

  const username = loginResponse.username || loginResponse.email?.split("@")[0];

  const response = await axios.get(`${BASE_URL}/deal/${dealId}/overview-pdf`, {
    headers: {
      ...getCommonHeaders(true),
      username: username,
    },
    responseType: "blob",
  });

  return response.data;
};

export const downloadDealPaymentReceiptPdf = async (
  dealId: string | number,
): Promise<Blob> => {
  const loginResponse = JSON.parse(
    localStorage.getItem("loginResponse") || "{}",
  );
  if (!loginResponse?.username && !loginResponse?.email) {
    throw new Error("User not authenticated");
  }

  const username = loginResponse.username || loginResponse.email?.split("@")[0];

  const response = await axios.get(
    `${BASE_URL}/payment/${dealId}/payment-pdf`,
    {
      headers: {
        ...getCommonHeaders(true),
        username: username,
      },
      responseType: "blob",
    },
  );

  return response.data;
};

export const getAllTemplates = async (): Promise<OpportunityCardTemplate[]> => {
  try {
    const response = await axios.get<OpportunityCardTemplate[]>(
      `${BASE_URL}/management/opportunity-card/templates`,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );

    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch templates"
    );
  }
};

export const formatPurseText = (purse: any): string => {
  if (purse == null) {
    return ""; // Handle null/undefined
  }
  if (typeof purse === "string" && purse.toLowerCase() === "negotiable") {
    return "Negotiable";
  } else {
    // If number, convert to string with formatting
    return Number(purse).toLocaleString("en-US"); // Example: 10000 -> '10,000'
  }
};

export const getActiveDeals = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/deal/active`, {
      headers: {
        "Content-Type": "application/json",
        USERNAME: JSON.parse(localStorage.getItem("loginResponse") || "{}")
          .username,
        ...getCommonHeaders(true),
      },
    });

    if (!response.data) {
      throw new Error("Failed to fetch active deals");
    }

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPostsByProfileId = async (
  profileId: string | number,
  page: number = 0,
  size: number = 10,
): Promise<any> => {
  try {
    const response = await axios.get(`${BASE_URL}/socialmedia/post/profile`, {
      params: {
        profileId,
        page,
        size,
      },
      headers: {
        ...getCommonHeaders(true),
      },
    });

    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data?.message || error.message || "Failed to fetch posts"
    );
  }
};

export const getOptionsBySource = async (
  optionsSource: string,
): Promise<string[]> => {
  try {
    const response = await axios.get(`${BASE_URL}${optionsSource}`, {
      headers: {
        ...getCommonHeaders(true),
      },
    });

    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data?.message ||
      error.message ||
      `Failed to fetch options from ${optionsSource}`
    );
  }
};

export const uploadRequiredDocuments = async (
  request: DocumentUploadRequestDto,
): Promise<DocumentUploadResponseDto> => {
  try {
    const response = await axios.post<DocumentUploadResponseDto>(
      `${BASE_URL}/deal/documents/upload`,
      request,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      },
    );

    return response.data;
  } catch (error: any) {
    console.error("Upload Documents Error:", error);
    throw (
      error.response?.data?.message || error.message || "Something went wrong"
    );
  }
};

export const createEvent = async (
  username: string,
  request: CreateEventRequest,
): Promise<EventDetailResponse> => {
  try {
    const response = await axios.post<EventDetailResponse>(
      `${BASE_URL}/event`,
      request,
      {
        headers: {
          ...getCommonHeaders(true),
          USERNAME: username,
        },
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllEvents = async (
  username: string,
): Promise<EventListResponse[]> => {
  try {
    const effectiveUsername = getEffectiveUsername(username);

    const headers: Record<string, string> = {
      ...getCommonHeaders(true),
    };

    if (effectiveUsername) {
      headers["USERNAME"] = effectiveUsername;
    }

    const response = await axios.get<EventListResponse[]>(`${BASE_URL}/event`, {
      headers,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

const getEffectiveUsername = (username?: string): string => {
  if (username && username.trim()) return username.trim();
  try {
    const stored = localStorage.getItem("username");
    if (stored && stored.trim()) return stored.trim();
    const userJson = localStorage.getItem("loginResponse");
    if (userJson) {
      const user = JSON.parse(userJson);
      return user.username || user.email?.split("@")[0] || "";
    }
  } catch {}
  return "";
};

export const getEventDetail = async (
  eventId: string,
  username?: string,
): Promise<EventDetailResponse> => {
  try {
    const headers: Record<string, string> = {
      ...getCommonHeaders(true),
    };

    const resolvedUsername = getEffectiveUsername(username);
    if (resolvedUsername) {
      headers.USERNAME = resolvedUsername;
    }

    const response = await axios.get<EventDetailResponse>(
      `${BASE_URL}/event/${eventId}`,
      {
        headers,
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateEvent = async (
  username: string,
  eventId: string,
  request: UpdateEventRequest,
): Promise<EventDetailResponse> => {
  try {
    const headers: Record<string, string> = {
      ...getCommonHeaders(true),
    };

    const resolvedUsername = getEffectiveUsername(username);
    if (resolvedUsername) {
      headers.USERNAME = resolvedUsername;
    }

    const response = await axios.put<EventDetailResponse>(
      `${BASE_URL}/event`,
      {
        ...request,
        eventId,
        id: eventId,
      },
      {
        headers,
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteEvent = async (
  username: string,
  eventId: string,
): Promise<MessageResponse> => {
  try {
    const headers: Record<string, string> = {
      ...getCommonHeaders(true),
    };

    const resolvedUsername = getEffectiveUsername(username);
    if (resolvedUsername) {
      headers.USERNAME = resolvedUsername;
    }

    const response = await axios.delete<MessageResponse>(
      `${BASE_URL}/event/${eventId}`,
      {
        headers,
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createMatchup = async (
  username: string,
  eventId: string,
  request: CreateMatchupRequest,
): Promise<MatchupResponse> => {
  try {
    const effectiveUsername = getEffectiveUsername(username);
    const headers: Record<string, string> = {
      ...getCommonHeaders(true),
    };

    if (effectiveUsername) {
      headers["USERNAME"] = effectiveUsername;
    }

    const response = await axios.post<MatchupResponse>(
      `${BASE_URL}/event/matchups/${eventId}`,
      request,
      {
        headers,
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getMatchupsByEvent = async (
  eventId: string,
  username?: string,
): Promise<MatchupResponse[]> => {
  try {
    const effectiveUsername = getEffectiveUsername(username);
    const headers: Record<string, string> = {
      ...getCommonHeaders(true),
    };

    if (effectiveUsername) {
      headers.USERNAME = effectiveUsername;
    }

    const response = await axios.get<MatchupResponse[]>(
      `${BASE_URL}/event/matchups/${eventId}`,
      {
        headers,
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getMatchup = async (
  matchupId: number,
  username?: string,
): Promise<MatchupResponse> => {
  try {
    const effectiveUsername = getEffectiveUsername(username);
    const headers: Record<string, string> = {
      ...getCommonHeaders(true),
    };

    if (effectiveUsername) {
      headers.USERNAME = effectiveUsername;
    }

    const response = await axios.get<MatchupResponse>(
      `${BASE_URL}/event/matchups/${matchupId}/matchup`,
      {
        headers,
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateMatchup = async (
  username: string,
  matchupId: number,
  request: UpdateMatchupRequest,
): Promise<MatchupResponse> => {
  try {
    const effectiveUsername = getEffectiveUsername(username);
    const headers: Record<string, string> = {
      ...getCommonHeaders(true),
    };

    if (effectiveUsername) {
      headers.USERNAME = effectiveUsername;
    }

    const response = await axios.put<MatchupResponse>(
      `${BASE_URL}/event/matchups/${matchupId}`,
      request,
      {
        headers,
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteMatchup = async (
  username: string,
  matchupId: number,
): Promise<MessageResponse> => {
  try {
    const effectiveUsername = getEffectiveUsername(username);
    const headers: Record<string, string> = {
      ...getCommonHeaders(true),
    };

    if (effectiveUsername) {
      headers.USERNAME = effectiveUsername;
    }

    const response = await axios.delete<MessageResponse>(
      `${BASE_URL}/event/matchups/${matchupId}`,
      {
        headers,
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateBoutOrder = async (
  username: string,
  matchupId: string,
  request: UpdateBoutOrderRequest,
): Promise<MatchupResponse> => {
  try {
    const effectiveUsername = getEffectiveUsername(username);

    const headers: Record<string, string> = {
      ...getCommonHeaders(true),
    };

    if (effectiveUsername) {
      headers["USERNAME"] = effectiveUsername;
    }

    const response = await axios.put<MatchupResponse>(
      `${BASE_URL}/event/matchups/${matchupId}/bout-order`,
      request,
      {
        headers,
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const downloadMatchupListPdf = async (
  username: string,
  eventId: string,
): Promise<Blob> => {
  try {
    const effectiveUsername = getEffectiveUsername(username);

    const headers: Record<string, string> = {
      ...getCommonHeaders(true),
    };

    if (effectiveUsername) {
      headers["USERNAME"] = effectiveUsername;
    }

    const response = await axios.get(
      `${BASE_URL}/event/${eventId}/matchups/pdf`,
      {
        headers,
        responseType: "blob",
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getGraphDetail = async (
  username: string,
  eventId: string,
): Promise<EventGraphDetailResponse> => {
  try {
    const effectiveUsername = getEffectiveUsername(username);

    const headers: Record<string, string> = {
      ...getCommonHeaders(true),
    };

    if (effectiveUsername) {
      headers["USERNAME"] = effectiveUsername;
    }

    const response = await axios.get<EventGraphDetailResponse>(
      `${BASE_URL}/event/${eventId}/graph-detail`,
      {
        headers,
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const shareEvent = async (
  username: string,
  eventId: string,
  request: ShareEventRequest,
): Promise<MessageResponse> => {
  try {
    const effectiveUsername = getEffectiveUsername(username);

    const headers: Record<string, string> = {
      ...getCommonHeaders(true),
    };

    if (effectiveUsername) {
      headers["USERNAME"] = effectiveUsername;
    }

    const response = await axios.post<MessageResponse>(
      `${BASE_URL}/event/${eventId}/share`,
      request,
      {
        headers,
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const reviewDealDocument = async (
  username: string,
  dealId: string,
  request: DocumentReviewRequestDto,
): Promise<DealResponseDto> => {
  const response = await axios.put<DealResponseDto>(
    `${BASE_URL}/deal/${dealId}/documents/review`,
    request,
    {
      headers: {
        ...getCommonHeaders(true),
        USERNAME: username,
      },
    },
  );

  return response.data;
};

export const inviteFighter = async (
  fighterId: string | number,
  eventId: string | number,
): Promise<string> => {
  try {
    const response = await axios.post<string>(
      `${BASE_URL}/management/contacts/invite/${fighterId}/event/${eventId}`,
      {},
      {
        headers: getCommonHeaders(true),
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getEventShareHistory = async (
  username: string,
  eventId: string,
): Promise<EventShareHistoryResponse[]> => {
  try {
    const effectiveUsername = getEffectiveUsername(username);

    const headers: Record<string, string> = {
      ...getCommonHeaders(true),
    };

    if (effectiveUsername) {
      headers.USERNAME = effectiveUsername;
    }

    const response = await axios.get<EventShareHistoryResponse[]>(
      `${BASE_URL}/event/${eventId}/share-history`,
      {
        headers,
      },
    );

    return response.data || [];
  } catch (error) {
    throw error;
  }
};

export const previewDealAgreement = async (
  dealData: DealsProposalsRequestDto,
): Promise<Blob> => {
  try {
    const response = await axios.post<Blob>(
      `${BASE_URL}/deal/agreement/preview`,
      dealData,
      { headers: getCommonHeaders(true), responseType: "blob" },
    );
    return response.data;
  } catch (error) {
    // With responseType "blob" the backend error JSON also arrives as a Blob, so read it back into an object.
    if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
      const errorText = await error.response.data.text();
      try {
        error.response.data = JSON.parse(errorText);
      } catch {
        // Not JSON, keep the error as it is.
      }
    }
    throw error;
  }
};

export const previewDealAgreementForUpdate = async (
  updateData: UpdateDealsProposalsRequestDto,
): Promise<Blob> => {
  const response = await axios.post<Blob>(
    `${BASE_URL}/deal/agreement/preview-update`,
    updateData,
    { headers: getCommonHeaders(true), responseType: "blob" },
  );

  return response.data;
};

export const getDealAgreement = async (
  dealId: string,
): Promise<DealAgreementResponseDto | null> => {
  const response = await axios.get<DealAgreementResponseDto>(
    `${BASE_URL}/deal/${dealId}/agreement`,
    { headers: getCommonHeaders(true) },
  );
  if (response.status === 204) return null;
  return response.data;
};

export const signDealAgreement = async (
  dealId: string,
  signatureImage: string,
): Promise<void> => {
  await axios.post(
    `${BASE_URL}/deal/${dealId}/agreement/sign`,
    { signatureImage },
    { headers: getCommonHeaders(true) },
  );
};

export const getMoneyFlow = async (
  range: MoneyFlowRange,
): Promise<MoneyFlowResponseDto> => {
  const loginResponse = JSON.parse(
    localStorage.getItem("loginResponse") || "{}",
  );

  const response = await axios.get<MoneyFlowResponseDto>(
    `${BASE_URL}/deal/dashboard/money-flow`,
    {
      params: { range },
      headers: {
        USERNAME: loginResponse.username,
        ...getCommonHeaders(true),
      },
    },
  );

  return response.data;
};

export const getFighterDashboardStats =
  async (): Promise<FighterDashboardStatsDto> => {
    const response = await axios.get<FighterDashboardStatsDto>(
      `${BASE_URL}/deal/dashboard/fighter-stats`,
      {
        headers: getCommonHeaders(true),
      },
    );

    return response.data;
  };

export const getDashboardPendingActions =
  async (): Promise<DashboardPendingActionsResponseDto> => {
    const response = await axios.get<DashboardPendingActionsResponseDto>(
      `${BASE_URL}/deal/dashboard/pending-actions`,
      {
        headers: getCommonHeaders(true),
      },
    );

    return response.data;
  };

export const getTotalFunds = async (
  range: MoneyFlowRange,
): Promise<TotalFundsDto> => {
  const response = await axios.get<TotalFundsDto>(
    `${BASE_URL}/deal/dashboard/total-funds`,
    {
      params: { range },
      headers: getCommonHeaders(true),
    },
  );

  return response.data;
};

export const getEventDashboardStats =
  async (): Promise<EventDashboardStatsDto> => {
    const response = await axios.get<EventDashboardStatsDto>(
      `${BASE_URL}/event/dashboard/stats`,
      {
        headers: getCommonHeaders(true),
      },
    );

    return response.data;
  };

export const getDealInsights = async (
  dealStatus: "ACTIVE" | "CLOSED",
): Promise<DealInsightsDto> => {
  const response = await axios.get<DealInsightsDto>(
    `${BASE_URL}/deal/dashboard/insights`,
    {
      params: { dealStatus },
      headers: getCommonHeaders(true),
    },
  );

  return response.data;
};

export const getCurrentEvents = async (): Promise<EventListResponse[]> => {
  const response = await axios.get<EventListResponse[]>(
    `${BASE_URL}/event/dashboard/current`,
    {
      headers: getCommonHeaders(true),
    },
  );

  return response.data;
};

export const requestAccountDeletion = async (): Promise<{
  deletionScheduledAt?: string;
}> => {
  const response = await axios.delete(`${BASE_URL}/user/profile/account`, {
    headers: getCommonHeaders(true),
  });
  return response.data;
};

export const cancelAccountDeletion = async (): Promise<void> => {
  await axios.post(`${BASE_URL}/user/profile/account/restore`, null, {
    headers: getCommonHeaders(true),
  });
};

export const getCardViewStats = async (
  username: string,
  params: CardViewStatsParams = {},
): Promise<CardViewStatsResponseDto> => {
  const response = await axios.get<CardViewStatsResponseDto>(
    `${BASE_URL}/tracking/card-view-stats`,
    {
      headers: {
        ...getCommonHeaders(true),
        USERNAME: username,
      },
      params,
    },
  );
  return response.data;
};
